<script setup lang="ts">
/**
 * BaseBadge — components.css 의 .badge 계열 원자.
 *  type: info(기본 .badge) · ok · warn · danger · muted · gold
 * 상태색은 토큰(soft 배경 + 본색 텍스트) 경유 — hex 직접 박지 않음.
 * soft 배경색도 tailwind.config 에 등록된 유틸(bg-ok-soft 등)을 사용한다.
 */
import { computed } from 'vue';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '#/lib/utils';

const badgeVariants = cva(
  // .badge 공통: inline-flex · 11.5px · 700 · padding 4/10 · radius 6
  'inline-flex items-center gap-[5px] rounded-md px-2.5 py-1 text-[11.5px] font-bold tracking-[-0.01em]',
  {
    variants: {
      type: {
        // 기본 .badge — info-soft 배경 / blue 텍스트(=info)
        info: 'bg-info-soft text-brand-muted',
        // .badge.ok — ok-soft / ok
        ok: 'bg-ok-soft text-ok',
        // .badge.warn — warn-soft / warn
        warn: 'bg-warn-soft text-warn',
        // .badge.danger — danger-soft / danger
        danger: 'bg-danger-soft text-danger',
        // .badge.muted — bg-3 / muted
        muted: 'bg-bg-3 text-muted',
        // .badge.gold — gold-soft / gold-deep
        accent: 'bg-accent-soft text-accent-deep',
      },
    },
    defaultVariants: {
      type: 'info',
    },
  },
);

type BadgeVariantProps = VariantProps<typeof badgeVariants>;

/** 배지 상태색 — 디자인 시스템 카탈로그 등에서 재사용. */
export type BadgeType = NonNullable<BadgeVariantProps['type']>;

interface Props {
  /** 상태색 — 결제·문의·인증 상태 표현 */
  type?: BadgeType;
}

const props = withDefaults(defineProps<Props>(), {
  type: 'info',
});

const classes = computed(() => cn(badgeVariants({ type: props.type })));
</script>

<template>
  <span :class="classes">
    <slot />
  </span>
</template>
